import { useEffect, useState } from 'react';
import { HelpCircle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  useProfile,
  useRefreshSirene,
  useUpsertProfile,
} from '../hooks/use-dossier-company';
import { ProfileRevenuesSection } from './profile-revenues-section';
import { ProfileCertificationsSection } from './profile-certifications-section';
import { InterventionZonesSection } from './intervention-zones-section';
import { CommercialStrategySection } from './commercial-strategy-section';
import { AggregatedReferencesSection } from './aggregated-references-section';
import { OnboardingWizard } from './onboarding-wizard';
import { useInterventionZones } from '../hooks/use-dossier-company';
import type {
  CompanyIdentitySnapshot,
  CompanyProfile,
} from '../types/dossier-company.types';

export function ProfileTab() {
  const { data, isLoading } = useProfile();
  const profile = data?.profile ?? null;
  const company = data?.company ?? null;
  const upsert = useUpsertProfile();
  const refresh = useRefreshSirene();
  const { data: zones } = useInterventionZones(false);

  const [effectifTotal, setEffectifTotal] = useState('');
  const [effectifTechnique, setEffectifTechnique] = useState('');
  const [effectifDate, setEffectifDate] = useState('');
  const [correspondance, setCorrespondance] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => {
    if (profile) {
      setEffectifTotal(profile.effectifTotal?.toString() ?? '');
      setEffectifTechnique(profile.effectifTechnique?.toString() ?? '');
      setEffectifDate(profile.effectifDate?.substring(0, 10) ?? '');
      setCorrespondance(profile.correspondanceAdresse ?? '');
    }
  }, [profile]);

  async function handleSave() {
    await upsert.mutateAsync({
      effectifTotal: effectifTotal ? parseInt(effectifTotal, 10) : null,
      effectifTechnique: effectifTechnique ? parseInt(effectifTechnique, 10) : null,
      effectifDate: effectifDate || null,
      correspondanceAdresse: correspondance || null,
    });
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const completeness = computeCompleteness(profile, zones?.length ?? 0);

  return (
    <div className="space-y-6">
      {/* Indicateurs de fraîcheur + complétude */}
      <div className="grid gap-3 md:grid-cols-2">
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium">Données SIRENE</h4>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              {refresh.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              <span className="ml-2">Rafraîchir</span>
            </Button>
          </div>
          <SireneStatusBadge company={company} />
        </Card>

        <Card className="p-4">
          <div className="flex items-start justify-between mb-2 gap-2">
            <h4 className="text-sm font-medium">Complétude du profil</h4>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setWizardOpen(true)}
              className="h-7 text-xs"
            >
              <HelpCircle className="h-3.5 w-3.5 mr-1" />
              Relancer le wizard
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Progress value={completeness.percent} className="flex-1" />
            <span className="text-sm font-medium tabular-nums">
              {completeness.filled}/{completeness.total}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {completeness.percent}% renseigné · le scoring Sprint 7 pondèrera selon ce taux
          </p>
        </Card>
      </div>

      {/* Section 1 — Identité SIRENE */}
      <Card className="p-6">
        <h3 className="text-base font-semibold mb-4">Identité (SIRENE — lecture seule)</h3>
        <SireneIdentityDisplay company={company} />

        <Separator className="my-4" />

        <div className="space-y-2">
          <Label htmlFor="correspondance">Adresse de correspondance (si différente du siège)</Label>
          <Input
            id="correspondance"
            value={correspondance}
            onChange={(e) => setCorrespondance(e.target.value)}
            placeholder="ex. 25 rue de la Paix, 75002 Paris (bureau parisien)"
          />
        </div>
      </Card>

      {/* Section 2 — Chiffres clés */}
      <Card className="p-6 space-y-4">
        <h3 className="text-base font-semibold">Chiffres clés</h3>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="eff-total">Effectif total</Label>
            <Input
              id="eff-total"
              type="number"
              min={0}
              value={effectifTotal}
              onChange={(e) => setEffectifTotal(e.target.value)}
              placeholder="ex. 42"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="eff-tech">Effectif technique</Label>
            <Input
              id="eff-tech"
              type="number"
              min={0}
              value={effectifTechnique}
              onChange={(e) => setEffectifTechnique(e.target.value)}
              placeholder="ex. 35"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="eff-date">Date de validité</Label>
            <Input
              id="eff-date"
              type="date"
              value={effectifDate}
              onChange={(e) => setEffectifDate(e.target.value)}
            />
          </div>
        </div>

        <Separator />

        <div>
          <h4 className="text-sm font-medium mb-3">Chiffre d'affaires pluriannuel</h4>
          <ProfileRevenuesSection />
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={upsert.isPending}>
            {upsert.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enregistrer les chiffres clés
          </Button>
        </div>
      </Card>

      {/* Section 3 — Certifications */}
      <Card className="p-6">
        <h3 className="text-base font-semibold mb-4">Certifications & habilitations</h3>
        <ProfileCertificationsSection />
      </Card>

      {/* Section 4 — Sprint 5b.1 v2 : Zones d'intervention */}
      <InterventionZonesSection />

      {/* Section 5 — Sprint 5b.1 v2 : Stratégie commerciale */}
      <CommercialStrategySection />

      {/* Section 6 — Sprint 5b.1 v2 : Références agrégées */}
      <AggregatedReferencesSection />

      {/* Wizard relançable manuellement (n'efface pas onboardingCompanyDossierAt, cf. décision #23) */}
      <OnboardingWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </div>
  );
}

function SireneStatusBadge({ company }: { company: CompanyIdentitySnapshot | null }) {
  const lastFetchAt = company?.sireneLastFetchAt;
  const lastFetchStatus = company?.sireneLastFetchStatus;
  const etat = company?.etatAdministratif;

  // Alerte forte si la société est cessée (etatAdministratif = C).
  if (etat === 'C') {
    return (
      <Badge variant="destructive">
        ⚠ Société cessée (etatAdministratif = C) — bloquant pour candidater
      </Badge>
    );
  }

  if (!lastFetchAt) {
    return (
      <Badge variant="outline" className="bg-blue-50 text-blue-800">
        Cliquez « Rafraîchir » pour fetcher SIRENE
      </Badge>
    );
  }

  const ageDays = Math.floor(
    (Date.now() - new Date(lastFetchAt).getTime()) / (24 * 3600 * 1000),
  );
  const fetched = new Date(lastFetchAt).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  if (lastFetchStatus !== 'OK') {
    return (
      <Badge variant="destructive">
        Dernier fetch : {lastFetchStatus} ({fetched})
      </Badge>
    );
  }

  if (ageDays < 90) {
    return (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
        À jour — fetch du {fetched}
      </Badge>
    );
  }
  if (ageDays < 180) {
    return (
      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
        À rafraîchir — dernier fetch il y a {ageDays} jours
      </Badge>
    );
  }
  return (
    <Badge variant="destructive">
      Périmée — dernier fetch il y a {ageDays} jours
    </Badge>
  );
}

function SireneIdentityDisplay({ company }: { company: CompanyIdentitySnapshot | null }) {
  const c = company;
  if (!c) {
    return (
      <p className="text-sm text-muted-foreground">
        Pas encore de données SIRENE. Cliquez « Rafraîchir maintenant » sur le bandeau ci-dessus
        pour synchroniser depuis l'INSEE.
      </p>
    );
  }

  const fmtBool = (v: boolean | null): string => (v === true ? 'Oui' : v === false ? 'Non' : '—');
  const codeNaf =
    c.nomenclatureNafActive?.includes('NAF2025') && c.codeNaf2025
      ? c.codeNaf2025
      : c.codeNafRev2 ?? c.codeNaf2025 ?? '—';
  const cat = c.categorieEntreprise
    ? { MIC: 'Microentreprise', PME: 'PME', ETI: 'ETI', GE: 'Grande entreprise' }[c.categorieEntreprise] ??
      c.categorieEntreprise
    : '—';

  return (
    <div className="grid grid-cols-2 gap-4 text-sm">
      <div>
        <p className="text-muted-foreground">SIRET</p>
        <p className="font-mono">{c.siret ?? '—'}</p>
      </div>
      <div>
        <p className="text-muted-foreground">SIREN</p>
        <p className="font-mono">{c.siren ?? '—'}</p>
      </div>
      <div className="col-span-2">
        <p className="text-muted-foreground">Dénomination</p>
        <p className="font-medium">
          {c.denomination ?? '—'}
          {c.sigle && <span className="text-muted-foreground ml-2">({c.sigle})</span>}
        </p>
      </div>
      <div>
        <p className="text-muted-foreground">Catégorie juridique</p>
        <p>
          {c.categorieJuridiqueLabel ?? '—'}
          {c.categorieJuridique && (
            <span className="text-muted-foreground ml-1 text-xs">[{c.categorieJuridique}]</span>
          )}
        </p>
      </div>
      <div>
        <p className="text-muted-foreground">Catégorie entreprise</p>
        <p>{cat}</p>
      </div>
      <div>
        <p className="text-muted-foreground">Code NAF</p>
        <p className="font-mono">{codeNaf}</p>
      </div>
      <div>
        <p className="text-muted-foreground">Statut administratif</p>
        <p>
          {c.etatAdministratif === 'A' ? (
            <Badge className="bg-green-100 text-green-800">Actif</Badge>
          ) : c.etatAdministratif === 'C' ? (
            <Badge variant="destructive">Cessé</Badge>
          ) : (
            '—'
          )}
        </p>
      </div>
      <div>
        <p className="text-muted-foreground">Date de création légale</p>
        <p>
          {c.legalCreationDate ? new Date(c.legalCreationDate).toLocaleDateString('fr-FR') : '—'}
        </p>
      </div>
      <div>
        <p className="text-muted-foreground">Tranche effectifs SIRENE</p>
        <p>
          {c.trancheEffectifsSireneLabel ?? '—'}
          {c.anneeEffectifsSirene && (
            <span className="text-muted-foreground ml-1 text-xs">({c.anneeEffectifsSirene})</span>
          )}
        </p>
      </div>
      <div className="col-span-2">
        <p className="text-muted-foreground">Adresse siège</p>
        <p>
          {[c.siegeAdresseRue, c.siegeCodePostal, c.siegeCommune].filter(Boolean).join(', ') || '—'}
        </p>
      </div>
      <div className="col-span-2">
        <p className="text-muted-foreground">Marqueurs RSE</p>
        <div className="flex gap-1 mt-1">
          <Badge
            variant={c.estESS === true ? 'default' : 'outline'}
            className={c.estESS === true ? 'bg-green-100 text-green-800' : ''}
          >
            ESS : {fmtBool(c.estESS)}
          </Badge>
          <Badge
            variant={c.estSocieteMission === true ? 'default' : 'outline'}
            className={c.estSocieteMission === true ? 'bg-green-100 text-green-800' : ''}
          >
            Société à mission : {fmtBool(c.estSocieteMission)}
          </Badge>
        </div>
      </div>
    </div>
  );
}

function computeCompleteness(
  profile: CompanyProfile | null | undefined,
  zonesCount: number,
): { filled: number; total: number; percent: number } {
  // Sprint 5b.1 v2 — 3 nouveaux items : ≥1 zone, ≥1 extension CPV ou exclusion ou montant, ≥1 référence agrégée
  const total = 11;
  if (!profile) return { filled: 0, total, percent: 0 };
  let filled = 0;
  if (profile.effectifTotal !== null) filled += 1;
  if (profile.effectifTechnique !== null) filled += 1;
  if (profile.effectifDate !== null) filled += 1;
  if (profile.correspondanceAdresse !== null) filled += 1;
  if (profile.annualRevenues.length >= 1) filled += 1;
  if (profile.annualRevenues.length >= 2) filled += 1;
  if (profile.annualRevenues.length >= 3) filled += 1;
  if (profile.certifications.length >= 1) filled += 1;
  // Sprint 5b.1 v2
  if (zonesCount >= 1) filled += 1;
  if (
    (profile.targetSectorsCpv?.length ?? 0) > 0 ||
    (profile.excludedSectorsCpv?.length ?? 0) > 0 ||
    profile.targetAoMinAmountEur !== null ||
    profile.targetAoMaxAmountEur !== null
  ) {
    filled += 1;
  }
  if (
    (profile.aoWonCount ?? 0) > 0 ||
    (profile.aoWonSectorsCpv?.length ?? 0) > 0 ||
    (profile.referencesNote?.length ?? 0) > 0
  ) {
    filled += 1;
  }
  return { filled, total, percent: Math.round((filled / total) * 100) };
}
